"use client";

import { VenueLoading } from "../venue-list/Venue";
import ListCard from "@/components/views/list/card/VenueCard";
import InfiniteScroll from "@/components/InfiniteScroll";
import { fetchVenues } from "./fetcher";
import { useState, useEffect, useRef } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import { useUserLocation } from "@/context/UserLocationContext";

interface Props {
  initialData: any;
  service: Service;
  district: District.District;
  // nearbyVenuesTitle: string;
}

export default function NearbyVenuesByDistrictClientSection(props: Props) {
  const { district, service } = props;

  const [response, setResponse] = useState<Venue.List[]>(props.initialData || []);
  const [hasMore, setHasMore]: any = useState(
    props.initialData.length >= DEFAULT_DATA_LIMIT,
  );
  const [nextLoading, setNextLoading] = useState(false);

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
    sortByDistance,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  // Calculate distances when user location is available and venues are loaded
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && response.length > 0) {
      const venuesToCalculate = response
        .filter((venue: Venue.List) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: Venue.List) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, response, calculateDistances]);

  const next = async (page: number) => {
    if (nextLoading) return;
    if (!district) return;

    setNextLoading(true);
    const venues = await fetchVenues(service?.id, district?.id, page);
    setNextLoading(false);
    setResponse([...response, ...venues]);

    if (venues.length === 0 || venues.length < DEFAULT_DATA_LIMIT) {
      setHasMore(false);
    }
  };

  if (!props.district) return <></>;

  // When sorting by distance, the parent VenueClientSection renders a single
  // combined+sorted list of main and nearby venues, so we hide this section
  // to avoid showing the nearby venues twice (and to keep them in sorted order).
  if (sortByDistance) return <></>;

  if (!district?.title) return <NearbyVenuesSkeleton />;

  return (
    <>
      <InfiniteScroll
        isLoading={nextLoading}
        skeletonElementId="venue-card-skeleton"
        hasMore={hasMore}
        loading={<VenueLoading count={6} />}
        next={next}
        dataLength={response.length}
      >
        <div className="flex gap-2 flex-col py-4">
          {/* <div className="flex items-center gap-2">
            <HiMap className="text-2xl" />
            <h2 className="text-sm flex items-center gap-2 font-bold">
              {nearbyVenuesTitle}
            </h2>

            <div className="py-[2px] px-[6px] text-xs bg-primary text-white font-[500] rounded-full">
              {response.length}
            </div>
          </div> */}

          <div className="grid justify-items-center grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.isArray(response) &&
              response.map((venue: Venue.List, index: number) => {
                const distance = venueDistances.get(venue.id);
                return (
                  <ListCard titleHeadingLevel="h3" key={index} distance={distance} {...venue} index={index} />
                );
              })}
          </div>
        </div>
      </InfiniteScroll>
    </>
  );
}

function NearbyVenuesSkeleton() {
  return (
    <div className="flex gap-2 flex-col py-4">
      <Skeleton className="h-6 w-60" />
      <VenueLoading count={6} />
    </div>
  );
}
